/**
 * client.js — SystemOfWorkClient: async operations over an injected BlobHub session API.
 *
 * No React. Takes a plain dependency object so it can be reused in a standalone app.
 *
 * Constructor:
 *   new SystemOfWorkClient({ api, sessionManager, revisionId, getSessionId })
 *
 * - api           — the Api class instance (context/api.js)
 * - sessionManager — SessionManager instance (playground/session/manager.js)
 * - revisionId    — string; the current revision id
 * - getSessionId  — () => string; thunk returning the current root session id
 */

import {
  CELL_WORKFLOWS_ALIAS,
  WORKSPACES_ALIAS,
  HEARTBEAT_STATE_ALIAS,
  missionAlias,
  isMissionAlias,
  cellMetaThreadAlias
} from "./schema";

// Page size for the exhausting graph element listing — the loop fetches everything,
// so the largest legal page means the fewest round-trips.
const GRAPH_PAGE_LIMIT = 1000;

// Page size for the exhausting meta-thread listing.
const THREAD_PAGE_LIMIT = 100;

/**
 * Generate a client-side graph element id with the given prefix. The server
 * honours a client-provided element_id on add_vertex / add_edge (the graph
 * import path and createMission both rely on this), so generating ids here lets
 * callers insert the new element into the local store immediately — and delete
 * it again — without waiting for a reload.
 * @param {string} prefix
 * @returns {string}
 */
function generateElementId(prefix) {
  return `${prefix}_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
}

export default class SystemOfWorkClient {
  constructor({ api, sessionManager, revisionId, getSessionId }) {
    this._api = api;
    this._sessionManager = sessionManager;
    this._revisionId = revisionId;
    this._getSessionId = getSessionId;
  }

  // ---------------------------------------------------------------------------
  // Session resolution — F2 seam: single root session for now
  // ---------------------------------------------------------------------------

  /**
   * Return the session id to use for graph/thread ops for a given mission.
   * Currently returns the root session id (the F2 seam: one session per instance).
   * @param {string} _missionId - reserved for future multi-session support
   * @returns {string}
   */
  resolveSession(_missionId) {
    return this._getSessionId();
  }

  // ---------------------------------------------------------------------------
  // Read operations
  // ---------------------------------------------------------------------------

  /**
   * List all missions in the current session.
   * Filters sessionObjects by isMissionAlias and reads each mission's display
   * metadata (title, description, …) from the graph object's OWN metadata
   * (value.graph.metadata) — not from a vertex.
   * @returns {Promise<Array>}
   */
  async listMissions() {
    const sessionObjects = this._sessionManager.sessionObjects || [];
    return sessionObjects
      .filter(obj => isMissionAlias(obj.alias))
      .map(obj => {
        const missionId = obj.alias.slice("mission:".length);
        const val = this._sessionManager.getSessionData(obj.alias);
        const meta = (val && val.graph && val.graph.metadata) || {};
        return { id: missionId, ...meta };
      });
  }

  /**
   * List all cells (vertices labeled "cell") for a mission.
   * @param {string} missionId
   * @returns {Promise<Array>}
   */
  async listCells(missionId) {
    const sessionId = this.resolveSession(missionId);
    const alias = missionAlias(missionId);
    try {
      const elements = await this._listAllGraphElements(sessionId, alias, "vertex");
      return elements.filter(el => el.labels && el.labels.includes("cell"));
    } catch (e) {
      return [];
    }
  }

  /**
   * List all "depends_on" edges for a mission's graph.
   * Fetches the graph elements (which includes both vertices and edges) and
   * filters to edges with label "depends_on".
   *
   * Each edge keeps its `element_id` (and `rev` when present) so the editor can
   * delete it later via a delete_edge op, which is keyed by element_id.
   * @param {string} missionId
   * @returns {Promise<Array<{ element_id: string, from_id: string, to_id: string, rev?: number }>>}
   */
  async listDependsOn(missionId) {
    const sessionId = this.resolveSession(missionId);
    const alias = missionAlias(missionId);
    try {
      const elements = await this._listAllGraphElements(sessionId, alias, "edge");
      return elements
        .filter(el => el.type === "edge" && el.label === "depends_on")
        .map(el => ({ element_id: el.element_id, from_id: el.from_id, to_id: el.to_id, rev: el.rev }));
    } catch (e) {
      return [];
    }
  }

  /**
   * Fetch EVERY graph element of a given type, following the cursor across pages. The list API
   * returns one page at a time (`data.elements` + an opaque `data.cursor`, null on the last page);
   * a single un-paged call only sees the first page — which is why large missions previously
   * rendered just a subset of their cells.
   * @param {string} sessionId
   * @param {string} alias
   * @param {"vertex"|"edge"} type
   * @returns {Promise<Array>}
   */
  async _listAllGraphElements(sessionId, alias, type) {
    const all = [];
    let cursor = null;
    // Defensive page bound so a never-clearing cursor can't spin forever.
    for (let page = 0; page < 1000; page++) {
      const response = await this._api.listSessionGraphElements(
        this._revisionId, sessionId, alias, { type, cursor, limit: GRAPH_PAGE_LIMIT }
      );
      const data = (response && response.data) || {};
      all.push(...(data.elements || []));
      cursor = data.cursor || null;
      if (!cursor) break;
    }
    return all;
  }

  /**
   * Retrieve the cell-workflow and workspace template libraries from the session.
   * Each collection is returned as a list; absent or unrecognised data yields [].
   * @returns {Promise<{ workflows: Array, workspaces: Array }>}
   */
  async getTemplates() {
    const wfValue = this._sessionManager.getSessionData(CELL_WORKFLOWS_ALIAS);
    const wsValue = this._sessionManager.getSessionData(WORKSPACES_ALIAS);
    const parse = (val) => {
      if (!val) return [];
      if (val.type === 'message') {
        const meta = val.message && val.message.metadata;
        if (Array.isArray(meta)) return meta;
        return [];
      }
      if (val.type === 'json' && Array.isArray(val.json)) return val.json;
      return [];
    };
    return { workflows: parse(wfValue), workspaces: parse(wsValue) };
  }

  /**
   * Retrieve the heartbeat state from the session.
   * @returns {Promise<any>}
   */
  async getHeartbeat() {
    return this._sessionManager.getSessionData(HEARTBEAT_STATE_ALIAS) || null;
  }

  /**
   * List EVERY thread item in a cell's meta-thread, following the opaque cursor across pages
   * (chronological order). A single un-paged call only sees the server's first page (default 10
   * items), which previously left later thread items invisible to Mission Control.
   * @param {string} cellId
   * @returns {Promise<Array>}
   */
  async listMetaThread(cellId) {
    const sessionId = this._getSessionId();
    const alias = cellMetaThreadAlias(cellId);
    const all = [];
    let cursor = null;
    try {
      // Defensive page bound so a never-clearing cursor can't spin forever.
      for (let page = 0; page < 1000; page++) {
        const options = { ascending: true, limit: THREAD_PAGE_LIMIT };
        if (cursor) options.cursor = cursor;
        const response = await this._api.threadItemsList(this._revisionId, sessionId, alias, options);
        const data = (response && response.data) || {};
        all.push(...(data.items || []));
        cursor = data.cursor || null;
        if (!cursor) break;
      }
    } catch (e) {
      return all; // return whatever pages were collected before the error
    }
    return all;
  }

  // ---------------------------------------------------------------------------
  // Write operations
  // ---------------------------------------------------------------------------

  /**
   * Create a new mission graph session object. The mission's display metadata
   * (title, description) lives in the graph object's OWN metadata, not in a vertex.
   * Cells are added as vertices via apply_session_graph_mutations afterwards.
   * @param {{ id: string, title: string, description: string }} options
   * @returns {Promise<any>}
   */
  async createMission({ id, title, description }) {
    const alias = missionAlias(id);
    return this._sessionManager.uploadSessionObject(alias, {
      type: "graph",
      graph: {
        attributes: { id, created_at: new Date().toISOString() },
        metadata: { title, description }
      }
    });
  }

  /**
   * Update a mission's display metadata (title and/or description).
   *
   * A mission's display fields live in the graph object's OWN metadata
   * (value.graph.metadata), NOT in a vertex. This reads the current graph
   * envelope, merges the changed field(s) into its metadata — preserving the
   * existing attributes and any other metadata keys — and re-uploads the
   * envelope.
   *
   * Re-uploading the graph envelope is NON-DESTRUCTIVE to the mission's cells:
   * the graph's vertices/edges are a separate server-side store, mutated only
   * through apply_session_graph_mutations; the envelope value carries just
   * attributes + metadata (it never holds the vertices). This is exactly the
   * shape the platform's own graph editor saves (editor/details/content.js
   * treats a "graph" value as envelope-only — attributes + metadata).
   * @param {string} missionId
   * @param {{ title?: string, description?: string }} changed
   * @returns {Promise<any>}
   */
  async setMissionProps(missionId, changed) {
    const alias = missionAlias(missionId);
    const current = this._sessionManager.getSessionData(alias) || {};
    const graph = current.graph || {};
    const attributes = graph.attributes || {};
    const metadata = { ...(graph.metadata || {}), ...changed };
    return this._sessionManager.uploadSessionObject(alias, {
      type: "graph",
      graph: { attributes, metadata }
    });
  }

  /**
   * Add a new cell (with status "draft") to a mission's graph.
   *
   * Generates the cell's element_id client-side and returns the created cell
   * object ({ element_id, labels, props }) so the caller can insert it into the
   * local store immediately — no reload required.
   * @param {string} missionId
   * @param {{ title: string, cellWorkflow: string }} cellProps - plus any additional props
   * @returns {Promise<{ element_id: string, labels: string[], props: Object }>}
   */
  async addCell(missionId, { title, cellWorkflow, ...rest }) {
    const sessionId = this.resolveSession(missionId);
    const alias = missionAlias(missionId);
    const elementId = generateElementId("c");
    const props = { title, cell_workflow: cellWorkflow, status: "draft", ...rest };
    await this._api.applySessionGraphMutations(
      this._revisionId,
      sessionId,
      alias,
      [
        {
          op: "add_vertex",
          labels: ["cell"],
          element_id: elementId,
          props
        }
      ]
    );
    return { element_id: elementId, labels: ["cell"], props };
  }

  /**
   * Update arbitrary props on an existing cell vertex.
   * @param {string} missionId
   * @param {string} cellId
   * @param {Object} props
   * @param {string} [ifRev] - optional optimistic-concurrency revision guard
   * @returns {Promise<any>}
   */
  async setCellProps(missionId, cellId, props, ifRev) {
    const sessionId = this.resolveSession(missionId);
    const alias = missionAlias(missionId);
    const op = {
      op: "set_vertex_props",
      element_id: cellId,
      props
    };
    if (ifRev !== undefined) {
      op.if_rev = ifRev;
    }
    return this._api.applySessionGraphMutations(
      this._revisionId,
      sessionId,
      alias,
      [op]
    );
  }

  /**
   * Add a "depends_on" edge between two cells.
   *
   * Generates the edge's element_id client-side and returns the created edge
   * ({ element_id, from_id, to_id }) so the caller can insert it into the local
   * store immediately and delete it later by element_id — no reload required.
   * @param {string} missionId
   * @param {string} fromId - id of the dependent cell
   * @param {string} toId - id of the dependency cell
   * @returns {Promise<{ element_id: string, from_id: string, to_id: string }>}
   */
  async addDependsOn(missionId, fromId, toId) {
    const sessionId = this.resolveSession(missionId);
    const alias = missionAlias(missionId);
    const elementId = generateElementId("e");
    await this._api.applySessionGraphMutations(
      this._revisionId,
      sessionId,
      alias,
      [
        {
          op: "add_edge",
          label: "depends_on",
          element_id: elementId,
          from_id: fromId,
          to_id: toId
        }
      ]
    );
    return { element_id: elementId, from_id: fromId, to_id: toId };
  }

  /**
   * Delete a cell vertex from a mission's graph. The server removes incident
   * edges as part of the delete_vertex op.
   * @param {string} missionId
   * @param {string} cellId - the cell's element_id
   * @param {number} [ifRev] - optional optimistic-concurrency revision guard
   * @returns {Promise<any>}
   */
  async deleteCell(missionId, cellId, ifRev) {
    const sessionId = this.resolveSession(missionId);
    const alias = missionAlias(missionId);
    const op = { op: "delete_vertex", element_id: cellId };
    if (ifRev !== undefined) {
      op.if_rev = ifRev;
    }
    return this._api.applySessionGraphMutations(this._revisionId, sessionId, alias, [op]);
  }

  /**
   * Delete a depends_on edge from a mission's graph by its element_id.
   * @param {string} missionId
   * @param {string} edgeId - the edge's element_id
   * @param {number} [ifRev] - optional optimistic-concurrency revision guard
   * @returns {Promise<any>}
   */
  async deleteEdge(missionId, edgeId, ifRev) {
    const sessionId = this.resolveSession(missionId);
    const alias = missionAlias(missionId);
    const op = { op: "delete_edge", element_id: edgeId };
    if (ifRev !== undefined) {
      op.if_rev = ifRev;
    }
    return this._api.applySessionGraphMutations(this._revisionId, sessionId, alias, [op]);
  }

  /**
   * Activate a cell — set its status to "planned".
   * @param {string} missionId
   * @param {string} cellId
   * @returns {Promise<any>}
   */
  async activateCell(missionId, cellId) {
    const sessionId = this.resolveSession(missionId);
    const alias = missionAlias(missionId);
    return this._api.applySessionGraphMutations(
      this._revisionId,
      sessionId,
      alias,
      [
        {
          op: "set_vertex_props",
          element_id: cellId,
          props: { status: "planned" }
        }
      ]
    );
  }

  /**
   * Post a text answer to a cell's meta-thread (e.g. resolving a pending_prompt).
   * @param {string} missionId - used only for session resolution
   * @param {string} cellId
   * @param {string} text
   * @returns {Promise<any>}
   */
  async answerInput(missionId, cellId, text) {
    const sessionId = this.resolveSession(missionId);
    const alias = cellMetaThreadAlias(cellId);
    const content = [{ type: "text", text }];
    return this._api.threadItemPost(
      this._revisionId,
      sessionId,
      alias,
      content
    );
  }

  /**
   * Persist the cell-workflow template library to the session.
   * @param {Array} list - the full cell_workflows list to save
   * @returns {Promise<any>}
   */
  async saveWorkflows(list) {
    const value = { type: 'message', message: { metadata: list } };
    return this._sessionManager.uploadSessionObject(CELL_WORKFLOWS_ALIAS, value);
  }

  /**
   * Persist the workspace template library to the session.
   * @param {Array} list - the full workspaces list to save
   * @returns {Promise<any>}
   */
  async saveWorkspaces(list) {
    const value = { type: 'message', message: { metadata: list } };
    return this._sessionManager.uploadSessionObject(WORKSPACES_ALIAS, value);
  }
}
